import { useState } from 'react';
import StepHeader from './StepHeader';
import PasoCategoria from './PasoCategoria';
import PasoDatos from './PasoDatos';
import PasoFechaHora, { momentoAhora } from './PasoFechaHora';
import PasoUbicacion from './PasoUbicacion';
import PasoEvidencia from './PasoEvidencia';
import PasoResumen from './PasoResumen';
import PasoExito from './PasoExito';
import useCategorias from '../../hooks/useCategorias';
import useIncidencias from '../../hooks/useIncidencias';
import useConfiguracion from '../../hooks/useConfiguracion';
import { useWeather } from '../../hooks/useWeather';
import '../../styles/ReportWizard.css';
import '../../styles/PasoUbicacion.css';
import '../../styles/PasoEvidencia.css';
import '../../styles/Seguimiento.css';

// Función (no objeto estático) para que `momentoAhora()` se calcule al abrir
// el wizard, no al importar el módulo — si no, "ahora" quedaría congelado en
// el momento en que cargó la página, no en el que el ciudadano reporta.
const crearDraftInicial = () => ({
  categoriaId: null,
  esAnonimo: true,
  alias: '',
  tipoDocumento: 'DNI',
  numeroDocumento: '',
  nombres: '',
  apellidos: '',
  telefono: '',
  correo: '',
  consentimiento: false,
  ocurridoEn: momentoAhora(),
  direccion: '',
  referencia: '',
  lat: null,
  lng: null,
  // Vía elegida del catálogo (auxiliar al mapa) — excluyente con lat/lng al
  // enviar (ver `enviarReporte`), aunque lat/lng se mantengan igual para
  // centrar el mini-mapa del resumen y la marca de agua del clima.
  viaId: null,
  evidencias: [],
  descripcion: '',
});

// {hora:'02', minuto:'49', periodo:'PM'} -> '14:49:00' (el backend espera 24h)
const hora24 = (ocurridoEn) => {
  let h = parseInt(ocurridoEn.hora, 10) || 0;
  if (ocurridoEn.periodo === 'PM' && h !== 12) h += 12;
  if (ocurridoEn.periodo === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${ocurridoEn.minuto || '00'}:00`;
};

const PANTALLA_PASOS = 'pasos';
const PANTALLA_RESUMEN = 'resumen';
const PANTALLA_EXITO = 'exito';

/**
 * Orquesta el flujo de reporte de 5 pasos (README diseño, sección 5.2-5.9).
 * Cubre el flujo completo (HU-002 Fases 2-5): pasos 1-5, Resumen, envío real
 * al backend, y pantalla de éxito con código + QR. El borrador vive acá, en
 * memoria, mientras el wizard está montado.
 */
const ReportWizard = ({ onCerrar, onVerTicket, onEnviarEvidenciaEnSegundoPlano }) => {
  const [paso, setPaso] = useState(1);
  const [pantalla, setPantalla] = useState(PANTALLA_PASOS);
  const [draft, setDraft] = useState(crearDraftInicial);
  const [errorEnvio, setErrorEnvio] = useState('');
  const [ticketCreado, setTicketCreado] = useState(null);
  const { categorias } = useCategorias();
  const { loading: enviando, crearIncidencia } = useIncidencias();
  const { configuracion } = useConfiguracion();

  // Clima real para la marca de agua (HU-005 Fase 3) — solo se consulta
  // Open-Meteo si la instancia tiene el clima activado en su configuración;
  // si no, se pasan coordenadas `null` y `useWeather` ni siquiera intenta el
  // fetch (evita una llamada de red innecesaria).
  const { temperature, weatherCode } = useWeather(
    configuracion.marca_agua_incluye_clima ? draft.lat : null,
    configuracion.marca_agua_incluye_clima ? draft.lng : null
  );

  const marcaAguaConfig = {
    nombreEntidad: configuracion.nombre_entidad,
    incluirFechaHora: configuracion.marca_agua_incluye_fecha_hora,
    incluirDireccion: configuracion.marca_agua_incluye_direccion,
    incluirClima: configuracion.marca_agua_incluye_clima,
    // Logo institucional (HU-008 Fase 4; toggle explícito en HU-014) — se
    // dibuja en la marca de agua solo si la instancia tiene uno configurado
    // Y el toggle de Configuración está activo; si no, la placa sigue
    // igual, solo sin logo (ver `addWatermark` en useMediaCapture.js).
    logo: configuracion.logo,
    incluirLogo: configuracion.marca_agua_incluye_logo,
  };

  const avanzar = () => setPaso((p) => Math.min(p + 1, 5));
  const retroceder = () => setPaso((p) => Math.max(p - 1, 1));
  const cambiarDraft = (cambios) => setDraft((d) => ({ ...d, ...cambios }));

  const enviarReporte = async () => {
    setErrorEnvio('');

    const resultado = await crearIncidencia({
      isAnonymous: draft.esAnonimo,
      categoriaId: draft.categoriaId,
      // Coordenadas y vía son excluyentes en el backend (Incidencia.clean()):
      // si el ciudadano eligió una vía del catálogo, se manda solo fk_via_id.
      latitud: draft.viaId ? null : draft.lat,
      longitud: draft.viaId ? null : draft.lng,
      fk_via_id: draft.viaId,
      direccion: draft.direccion,
      referencia: draft.referencia,
      fecha: draft.ocurridoEn.fecha,
      hora: hora24(draft.ocurridoEn),
      comentario: draft.descripcion,
      alias: draft.alias,
      nombres: draft.nombres,
      // El diseño nuevo solo pide "Apellidos" (un campo), el modelo del
      // backend tiene paterno/materno por separado — se manda todo a
      // apellidoPaterno y se deja materno vacío, no se le pide al ciudadano
      // dividir su propio apellido en dos cajas que el diseño no tiene.
      apellidoPaterno: draft.apellidos,
      documento: draft.numeroDocumento,
      telefono: draft.telefono,
      email: draft.correo,
    });

    if (!resultado.success) {
      setErrorEnvio(resultado.error || 'No se pudo enviar el reporte. Intenta de nuevo.');
      return;
    }

    // El ticket ya existe — se muestra de inmediato, no se hace esperar al
    // ciudadano a que termine de subir la evidencia (HU-008 Fase 7: antes
    // `await`ábamos todos los adjuntos acá mismo, y con datos móviles el
    // envío se sentía "colgado" varios segundos con el ticket ya creado
    // pero sin mostrar). La subida sigue en segundo plano, en el padre
    // (`MapaPrincipal`), que no se desmonta aunque el ciudadano cierre este
    // wizard — la dirección y el clima viajan como campos transitorios
    // para que el backend marque de agua los videos (HU-005 Fase 3) con el
    // mismo texto que ya se ve en la foto, nunca se guardan en el modelo.
    const climaTexto = configuracion.marca_agua_incluye_clima && temperature != null
      ? `${temperature}°C`
      : null;
    onEnviarEvidenciaEnSegundoPlano(draft.evidencias, resultado.incidenciaId, draft.lat, draft.lng, draft.direccion, climaTexto);

    setTicketCreado(resultado.ticket);
    setPantalla(PANTALLA_EXITO);
  };

  // Ubicación (paso 4) y evidencia/captura (paso 5, cuando abre la cámara)
  // toman toda la pantalla con su propia UI — no van dentro de la hoja
  // oscurecida que usan los pasos 1-3.
  if (paso === 4 && pantalla === PANTALLA_PASOS) {
    return (
      <PasoUbicacion
        ubicacion={draft}
        onCambiar={cambiarDraft}
        onContinuar={avanzar}
        onVolver={retroceder}
      />
    );
  }

  if (pantalla === PANTALLA_EXITO) {
    const categoriaNombre = categorias.find((c) => c.cat_inId === draft.categoriaId)?.cat_chNombre;
    return (
      <PasoExito
        ticket={ticketCreado}
        resumen={`${categoriaNombre || 'Incidencia'} — ${draft.direccion}`}
        onVerTicket={() => onVerTicket(ticketCreado)}
        onVolverAlMapa={onCerrar}
      />
    );
  }

  if (pantalla === PANTALLA_RESUMEN) {
    const categoriaSeleccionada = categorias.find((c) => c.cat_inId === draft.categoriaId);
    return (
      <div className="rw-overlay">
        <div className="rw-sheet">
          <StepHeader paso={5} onClose={onCerrar} onBack={() => setPantalla(PANTALLA_PASOS)} />
          <PasoResumen
            draft={draft}
            categoriaNombre={categoriaSeleccionada?.cat_chNombre}
            categoriaLogo={{ imagen: categoriaSeleccionada?.cat_fileLogo, iconoPreset: categoriaSeleccionada?.cat_chIconoPreset }}
            onEditarPaso={(n) => { setPantalla(PANTALLA_PASOS); setPaso(n); }}
            onVolver={() => setPantalla(PANTALLA_PASOS)}
            onEnviar={enviarReporte}
            enviando={enviando}
          />
          {errorEnvio && <p className="rw-error" style={{ padding: '0 20px 16px' }}>{errorEnvio}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="rw-overlay">
      <div className="rw-sheet">
        <StepHeader paso={paso} onClose={onCerrar} onBack={paso > 1 ? retroceder : null} />

        {paso === 1 && (
          <PasoCategoria
            categoriaId={draft.categoriaId}
            onSeleccionar={(categoriaId) => cambiarDraft({ categoriaId })}
            onContinuar={avanzar}
          />
        )}

        {paso === 2 && (
          <PasoDatos
            datos={draft}
            onCambiar={setDraft}
            onContinuar={avanzar}
            onVolver={retroceder}
          />
        )}

        {paso === 3 && (
          <PasoFechaHora
            ocurridoEn={draft.ocurridoEn}
            onCambiar={(ocurridoEn) => cambiarDraft({ ocurridoEn })}
            onContinuar={avanzar}
            onVolver={retroceder}
          />
        )}

        {paso === 5 && (
          <PasoEvidencia
            evidencias={draft.evidencias}
            descripcion={draft.descripcion}
            locationData={{ address: draft.direccion, latitude: draft.lat, longitude: draft.lng, temperature, weatherCode }}
            marcaAguaConfig={marcaAguaConfig}
            onCambiar={cambiarDraft}
            onContinuar={() => setPantalla(PANTALLA_RESUMEN)}
            onVolver={retroceder}
          />
        )}
      </div>
    </div>
  );
};

export default ReportWizard;
